/**
 * Browser geo engine: self-hosted WebR 0.6.0 + sf.
 * Runtime binaries load from same-origin /webr/0.6.0/ (production Caddy;
 * locally node_modules/webr/dist). Not webr.r-wasm.org.
 */
import { WebR } from "webr";
import type { Feature, Geometry } from "geojson";
import type { CallToolResult } from "@modelcontextprotocol/client";
import type { GeoComputeResult, GeoToolName } from "../../shared/geo.js";

export const WEBR_ENGINE = "webr-sf" as const;
/** Production already hosts WebR 0.6.0 here (do not re-vendor). Trailing slash required. */
export const WEBR_BASE_URL = "/webr/0.6.0/";
export const WEBR_REPO_URL = "https://repo.r-wasm.org/";

let boot: Promise<WebR> | null = null;

export async function initWebR(onStatus?: (msg: string) => void): Promise<WebR> {
  if (!boot) {
    boot = (async () => {
      onStatus?.("arrancando WebR 0.6.0 desde /webr/0.6.0/…");
      const webR = new WebR({
        baseUrl: WEBR_BASE_URL,
        repoUrl: WEBR_REPO_URL,
      });
      await webR.init();
      onStatus?.("instalando sf desde repo.r-wasm.org (primera corrida)…");
      await webR.installPackages(["sf"], { quiet: true });
      await webR.evalRVoid(`
        suppressPackageStartupMessages(library(sf))
        sf::sf_use_s2(TRUE)
      `);
      onStatus?.("WebR 0.6.0 + sf listo");
      return webR;
    })().catch((error) => {
      boot = null;
      throw error;
    });
  }
  return boot;
}

function asFeature(geometry: Geometry, properties: Record<string, unknown> = {}): Feature {
  return { type: "Feature", properties, geometry };
}

function pickFeature(parsed: unknown): Feature {
  if (!parsed || typeof parsed !== "object") {
    throw new Error("sf no devolvió GeoJSON válido");
  }
  const obj = parsed as { type?: string; features?: Feature[]; geometry?: Geometry };
  if (obj.type === "Feature" && obj.geometry) {
    return parsed as Feature;
  }
  if (obj.type === "FeatureCollection" && Array.isArray(obj.features) && obj.features[0]) {
    return obj.features[0];
  }
  if (obj.type && "coordinates" in obj) {
    return asFeature(parsed as Geometry);
  }
  throw new Error("No pude extraer un Feature del resultado de sf");
}

function assertGeometry(geometry: unknown, label = "geometría"): Geometry {
  if (!geometry || typeof geometry !== "object" || !("type" in geometry)) {
    throw new Error(`Hace falta una ${label} GeoJSON válida.`);
  }
  return geometry as Geometry;
}

async function writeGeom(webR: WebR, path: string, geometry: Geometry): Promise<void> {
  const collection = JSON.stringify({
    type: "FeatureCollection",
    features: [asFeature(geometry)],
  });
  await webR.FS.writeFile(path, new TextEncoder().encode(collection));
}

async function evalGeoJson(webR: WebR, code: string): Promise<Feature> {
  const json = await webR.evalRString(code);
  return pickFeature(JSON.parse(json));
}

const READ_G = `
    suppressPackageStartupMessages(library(sf))
    g <- sf::st_read("/home/web_user/in.geojson", quiet = TRUE)
    if (is.na(sf::st_crs(g))) sf::st_crs(g) <- 4326
`;

function writeOut(expr: string): string {
  return `
    ${READ_G}
    out <- ${expr}
    if (nrow(out) == 0 || all(sf::st_is_empty(out))) stop("sf devolvió geometría vacía")
    tf <- tempfile(fileext = ".geojson")
    sf::st_write(out, tf, driver = "GeoJSON", delete_dsn = TRUE, quiet = TRUE)
    paste(readLines(tf, warn = FALSE), collapse = "")
  `;
}

export async function computeGeoBufferWebr(
  geometry: Geometry,
  distanceMeters: number,
  onStatus?: (msg: string) => void,
): Promise<GeoComputeResult> {
  if (!Number.isFinite(distanceMeters) || distanceMeters <= 0) {
    throw new Error("La distancia debe ser un número positivo de metros.");
  }
  const geom = assertGeometry(geometry);
  const webR = await initWebR(onStatus);
  await writeGeom(webR, "/home/web_user/in.geojson", geom);
  const feature = await evalGeoJson(
    webR,
    writeOut(`sf::st_buffer(g, dist = ${distanceMeters})`),
  );
  feature.properties = {
    ...(feature.properties ?? {}),
    distanceMeters,
    engine: WEBR_ENGINE,
  };
  return { feature, distanceMeters, engine: WEBR_ENGINE };
}

export async function computeGeoCentroidWebr(
  geometry: Geometry,
  onStatus?: (msg: string) => void,
): Promise<GeoComputeResult> {
  const geom = assertGeometry(geometry);
  const webR = await initWebR(onStatus);
  await writeGeom(webR, "/home/web_user/in.geojson", geom);
  const feature = await evalGeoJson(webR, writeOut("sf::st_centroid(g)"));
  feature.properties = { ...(feature.properties ?? {}), engine: WEBR_ENGINE };
  return { feature, extras: [asFeature(geom)], engine: WEBR_ENGINE };
}

export async function computeGeoMeasureWebr(
  geometry: Geometry,
  onStatus?: (msg: string) => void,
): Promise<GeoComputeResult> {
  const geom = assertGeometry(geometry);
  const webR = await initWebR(onStatus);
  await writeGeom(webR, "/home/web_user/in.geojson", geom);
  const raw = await webR.evalRString(`
    ${READ_G}
    len <- tryCatch(as.numeric(sf::st_length(g))[1], error = function(e) NA_real_)
    ar <- tryCatch(as.numeric(sf::st_area(g))[1], error = function(e) NA_real_)
    paste(
      ifelse(is.finite(len), format(len, scientific = FALSE, trim = TRUE), "null"),
      ifelse(is.finite(ar), format(ar, scientific = FALSE, trim = TRUE), "null"),
      sep = "\t"
    )
  `);
  const [lenStr, areaStr] = raw.split("\t");
  const lengthMeters = lenStr && lenStr !== "null" ? Number(lenStr) : 0;
  const areaSqMeters = areaStr && areaStr !== "null" ? Number(areaStr) : 0;
  const feature = asFeature(geom, {
    lengthMeters,
    areaSqMeters,
    engine: WEBR_ENGINE,
  });
  return { feature, lengthMeters, areaSqMeters, engine: WEBR_ENGINE };
}

export async function computeGeoIntersectWebr(
  geometry: Geometry,
  other: Geometry,
  onStatus?: (msg: string) => void,
): Promise<GeoComputeResult> {
  const a = assertGeometry(geometry, "geometría A");
  const b = assertGeometry(other, "geometría B");
  const webR = await initWebR(onStatus);
  await writeGeom(webR, "/home/web_user/in.geojson", a);
  await writeGeom(webR, "/home/web_user/in2.geojson", b);
  try {
    const feature = await evalGeoJson(
      webR,
      `
        suppressPackageStartupMessages(library(sf))
        a <- sf::st_read("/home/web_user/in.geojson", quiet = TRUE)
        b <- sf::st_read("/home/web_user/in2.geojson", quiet = TRUE)
        if (is.na(sf::st_crs(a))) sf::st_crs(a) <- 4326
        if (is.na(sf::st_crs(b))) sf::st_crs(b) <- 4326
        out <- sf::st_intersection(a, b)
        if (nrow(out) == 0 || all(sf::st_is_empty(out))) stop("empty")
        tf <- tempfile(fileext = ".geojson")
        sf::st_write(out, tf, driver = "GeoJSON", delete_dsn = TRUE, quiet = TRUE)
        paste(readLines(tf, warn = FALSE), collapse = "")
      `,
    );
    feature.properties = { ...(feature.properties ?? {}), engine: WEBR_ENGINE };
    return {
      feature,
      extras: [asFeature(a), asFeature(b)],
      engine: WEBR_ENGINE,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (/empty/i.test(message)) {
      throw new Error("Las geometrías no se intersectan.");
    }
    throw error;
  }
}

export async function computeGeoWebr(
  name: GeoToolName,
  args: Record<string, unknown>,
  onStatus?: (msg: string) => void,
): Promise<GeoComputeResult> {
  switch (name) {
    case "geo.buffer":
      return computeGeoBufferWebr(
        args.geometry as Geometry,
        Number(args.distanceMeters),
        onStatus,
      );
    case "geo.centroid":
      return computeGeoCentroidWebr(args.geometry as Geometry, onStatus);
    case "geo.measure":
      return computeGeoMeasureWebr(args.geometry as Geometry, onStatus);
    case "geo.intersect":
      return computeGeoIntersectWebr(
        args.geometry as Geometry,
        args.other as Geometry,
        onStatus,
      );
    default:
      throw new Error(`Herramienta desconocida: ${name}`);
  }
}

export function webrResultToTool(result: GeoComputeResult): CallToolResult {
  const payload = result.feature ?? result;
  return {
    content: [{ type: "text", text: JSON.stringify(payload) }],
    structuredContent: result,
  };
}

export function webrErrorToTool(error: unknown): CallToolResult {
  const message =
    error instanceof Error ? error.message : "WebR no pudo calcular la herramienta";
  return {
    isError: true,
    content: [{ type: "text", text: message }],
  };
}
